import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Plus, X, Music, User, Building2, Link, Trash2 } from 'lucide-react';
import { supabase } from '../../../backend/services/supabase';

interface CampaignAssociation {
  association_id: string;
  entity_type: 'release' | 'artist' | 'label';
  entity_id: string;
  entity_name: string;
  notes?: string;
  created_at: string;
}

interface CampaignAssociationsProps {
  campaignId: string;
  campaignType?: 'internal' | 'meta';
  campaignName: string;
  onAssociationsChange?: (associations: CampaignAssociation[]) => void;
}

interface EntityOption {
  id: string;
  name: string;
  type: 'release' | 'artist' | 'label';
  subtitle?: string;
}

const CampaignAssociations: React.FC<CampaignAssociationsProps> = ({
  campaignId,
  campaignType = 'meta',
  campaignName,
  onAssociationsChange
}) => {
  const [associations, setAssociations] = useState<CampaignAssociation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [entityOptions, setEntityOptions] = useState<EntityOption[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<EntityOption | null>(null);
  const [notes, setNotes] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [entityType, setEntityType] = useState<'release' | 'artist' | 'label'>('release');
  const [submitting, setSubmitting] = useState(false);

  // Fetch existing associations
  const fetchAssociations = async () => {
    try {
      setLoading(true);
      const session = await supabase.auth.getSession();
      const response = await fetch(`/api/campaigns/associations?campaign_id=${campaignId}&campaign_type=${campaignType}`, {
        headers: {
          'Authorization': `Bearer ${session.data.session?.access_token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch associations');
      }

      const data = await response.json();
      setAssociations(data.associations || []);
      onAssociationsChange?.(data.associations || []);
    } catch (error: any) {
      console.error('Error fetching associations:', error);
      toast.error(`Failed to load associations: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch entity options for dropdown
  const fetchEntityOptions = async (type: 'release' | 'artist' | 'label', search: string = '') => {
    try {
      let query = supabase.from(type === 'label' ? 'labels' : `${type}s`);
      
      if (type === 'release') {
        query = query.select('id, title, artist:artists(name)');
        if (search) {
          query = query.ilike('title', `%${search}%`);
        }
      } else if (type === 'artist') {
        query = query.select('id, name, contact:contacts(name)');
        if (search) {
          query = query.ilike('name', `%${search}%`);
        }
      } else {
        query = query.select('id, name, description');
        if (search) {
          query = query.ilike('name', `%${search}%`);
        }
      }

      const { data, error } = await query.limit(20);

      if (error) {
        throw error;
      }

      const options: EntityOption[] = (data || []).map((item: any) => ({
        id: item.id,
        name: item.name || item.title,
        type,
        subtitle: type === 'release' 
          ? item.artist?.name 
          : type === 'artist' 
            ? item.contact?.name 
            : item.description
      }));

      setEntityOptions(options);
    } catch (error: any) {
      console.error('Error fetching entity options:', error);
      toast.error(`Failed to load ${type}s: ${error.message}`);
    }
  };

  // Add new association
  const handleAddAssociation = async () => {
    if (!selectedEntity) {
      toast.error('Please select an entity to associate');
      return;
    }

    try {
      setSubmitting(true);
      const session = await supabase.auth.getSession();
      const response = await fetch('/api/campaigns/associations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.data.session?.access_token}`
        },
        body: JSON.stringify({
          campaign_id: campaignId,
          campaign_type: campaignType,
          entity_type: selectedEntity.type,
          entity_id: selectedEntity.id,
          notes: notes.trim() || undefined
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create association');
      }

      toast.success(`Associated ${selectedEntity.name} with campaign`);
      setShowAddModal(false);
      setSelectedEntity(null);
      setNotes('');
      setSearchTerm('');
      await fetchAssociations();
    } catch (error: any) {
      console.error('Error adding association:', error);
      toast.error(`Failed to add association: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Remove association
  const handleRemoveAssociation = async (association: CampaignAssociation) => {
    if (!confirm(`Remove association with ${association.entity_name}?`)) {
      return;
    }

    try {
      const session = await supabase.auth.getSession();
      const response = await fetch(`/api/campaigns/associations?campaign_id=${campaignId}&campaign_type=${campaignType}&entity_type=${association.entity_type}&entity_id=${association.entity_id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.data.session?.access_token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to remove association');
      }

      toast.success(`Removed association with ${association.entity_name}`);
      await fetchAssociations();
    } catch (error: any) {
      console.error('Error removing association:', error);
      toast.error(`Failed to remove association: ${error.message}`);
    }
  };

  // Get icon for entity type
  const getEntityIcon = (entityType: string) => {
    switch (entityType) {
      case 'release': return <Music className="h-4 w-4" />;
      case 'artist': return <User className="h-4 w-4" />;
      case 'label': return <Building2 className="h-4 w-4" />;
      default: return <Link className="h-4 w-4" />;
    }
  };


  useEffect(() => {
    fetchAssociations();
  }, [campaignId, campaignType]);

  useEffect(() => {
    if (showAddModal) {
      fetchEntityOptions(entityType, searchTerm);
    }
  }, [showAddModal, entityType, searchTerm]);

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded"></div>
            <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-5/6"></div>
            <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-4/6"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Music Associations
        </h3>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn btn-primary btn-sm flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Association
        </button>
      </div>

      {associations.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <Link className="h-12 w-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
          <p className="text-sm">No associations yet</p>
          <p className="text-xs mt-1">Link this campaign to releases, artists, or labels</p>
        </div>
      ) : (
        <div className="space-y-3">
          {associations.map((association) => (
            <div
              key={association.association_id}
              className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-center gap-3 flex-1">
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                  {getEntityIcon(association.entity_type)}
                  <span className="text-xs font-medium capitalize">
                    {association.entity_type}
                  </span>
                </div>
                <div className="flex-1">
                  <div className="font-medium text-gray-900 dark:text-white">
                    {association.entity_name}
                  </div>
                  {association.notes && (
                    <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {association.notes}
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={() => handleRemoveAssociation(association)}
                className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                title="Remove association"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add Association Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Add Association
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Entity Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Entity Type
                </label>
                <div className="flex gap-2">
                  {(['release', 'artist', 'label'] as const).map((type) => (
                    <button
                      key={type}
                      onClick={() => {
                        setEntityType(type);
                        setSelectedEntity(null);
                        setSearchTerm('');
                      }}
                      className={`px-3 py-2 rounded-md text-sm font-medium capitalize transition-colors ${
                        entityType === type
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {getEntityIcon(type)}
                      <span className="ml-1">{type}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Entity Search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Search {entityType}s
                </label>
                <input
                  type="text"
                  placeholder={`Search for ${entityType}s...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              {/* Entity Selection */}
              {entityOptions.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select {entityType}
                  </label>
                  <div className="max-h-40 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-md">
                    {entityOptions.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => setSelectedEntity(option)}
                        className={`w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                          selectedEntity?.id === option.id
                            ? 'bg-blue-50 dark:bg-blue-900/50 border-l-2 border-blue-500'
                            : ''
                        }`}
                      >
                        <div className="font-medium text-gray-900 dark:text-white">
                          {option.name}
                        </div>
                        {option.subtitle && (
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {option.subtitle}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notes (optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any additional notes about this association..."
                  rows={3}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 btn btn-secondary"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                onClick={handleAddAssociation}
                disabled={!selectedEntity || submitting}
                className="flex-1 btn btn-primary"
              >
                {submitting ? 'Adding...' : 'Add Association'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignAssociations;
